import dayjs from 'dayjs';
import moment from 'moment/moment';
import {colorHelpers, dateHelpers, objectHelpers, subscriptionsHelpers} from '../../utils/helpers';

const dateFilterRanges = {
  CURRENT_MONTH: 'current-month',
  PREVIOUS_MONTH: 'previous-month',
  YEARLY: 'yearly'
}

const expendituresTypes = {
  ALL: 'all',
  EXPENSES: 'expenses',
  SUBSCRIPTIONS: 'subscriptions'
}

const {CURRENT_MONTH, YEARLY} = dateFilterRanges;

const {getSpendingServicesAmount, getSpendingServicesList} = subscriptionsHelpers;

const getFormattedSpending = (item) => {
  let isPersonalSpending = item?.card_id;
  return {
    ...item,
    actual: item.amount || 0,
    budget: item.budget || 0,
    service: isPersonalSpending
      ? { logo: item?.owner?.logo, name: item?.owner?.full_name}
      : { logo: item.service_logo, name: item.service }
  }
};

const dayFilter = (i, day) => i.day === day;

const dateFormat = 'YYYY-MM';


const overviewPageUtils = {
  /**
   * Adjusts the query object for yearly comparison by subtracting one year from the start and end dates.
   * @param {Object} query - The original query object with start_date and end_date.
   * @returns {Object} The adjusted query object for yearly comparison.
   */
  adjustPreviousQueryForYearly: (query) => ({
    ...query,
    start_date: dayjs(query.start_date).subtract(1, 'year').format(dateFormat),
    end_date: dayjs(query.end_date).subtract(1, 'year').format(dateFormat),
  }),

  /**
   * Adjusts the query object for monthly comparison by subtracting one month from the given month and year.
   * @param {Object} query - The original query object with month and year.
   * @returns {Object} The adjusted query object for monthly comparison.
   */
  adjustPreviousQueryForMonthly: (query = {}) => {
    const adjustedDate = dayjs()
      .set('month', query.month - 1)
      .set('year', query.year)
      .subtract(1, 'month');
    return {
      ...query,
      month: adjustedDate.month() + 1,
      year: adjustedDate.year(),
    };
  },

  /**
   * Calculates chart data for subscriptions based on the selected range (monthly or yearly).
   * @param {Object} params - Parameters for calculation.
   * @param {string} params.range - The selected range (CURRENT_MONTH, YEARLY).
   * @param {Array} params.actualSpending - List of actual spending data.
   * @param {Array} params.expectedSpending - List of expected spending data.
   * @param {string} params.expenditure - Expenditure type.
   * @returns {Array} Chart data for subscriptions.
   */
  calculateSubscriptionsChart: ({range, actualSpending, expectedSpending, expenditure}) => {
    const {day: currentDayNum} = dateHelpers.getCurrentDate();
    const daisInMonth = dayjs().daysInMonth();
    let expected = 0, current = 0;
    if (range === YEARLY) {
      const today = moment();
      let monthIndex = today.month();
      let currentMonthInformation, currentMonthSpending, expectedMonthSpending, label;
      return objectHelpers.range(1, 12).map(i => {
        monthIndex = monthIndex === 12 ? 1 : monthIndex + 1;
        today.set('month', monthIndex);
        label = today.format('MMM');
        currentMonthInformation = {
          label,
          name: label
        };
        currentMonthSpending = actualSpending.find(d => d.month === monthIndex);
        expectedMonthSpending = expectedSpending.find(d => d.month === monthIndex);
        current += currentMonthSpending?.amount || 0;
        expected += expectedMonthSpending?.amount || currentMonthSpending?.expected_amount || 0;
        return {
          ...currentMonthInformation,
          current,
          expected,
          isActive: i === 12
        };
      });
    } else {
      const isCurrentMonthSpending = range === CURRENT_MONTH;
      const monthDate = isCurrentMonthSpending ? moment() : moment().subtract(1, 'month');
      return objectHelpers.range(1, daisInMonth).map(day => {
        let date = monthDate.set('date', day);
        let currentDayInformation = {
          date: date.format('YYYY-MM-DD'),
          label: date.format('Do MMMM'),
          name: day
        };
        let currentDaySpending = actualSpending.find(i => dayFilter(i, day));
        let expectedDaySpending = expectedSpending.find(i => dayFilter(i, day));
        if (currentDaySpending) {
          current += getSpendingServicesAmount(currentDaySpending);
        }

        if (expectedDaySpending) {
          expected += getSpendingServicesAmount(expectedDaySpending);
        } else {
          expected += getSpendingServicesAmount(currentDaySpending, 'expected_amount');
        }

        currentDayInformation = {...currentDayInformation, expected};
        if (isCurrentMonthSpending) {
          if (day <= currentDayNum) currentDayInformation = {...currentDayInformation, current};
          if (day === currentDayNum) currentDayInformation = {...currentDayInformation, isActive: true};
        } else {
          currentDayInformation = {...currentDayInformation, current};
        }
        return currentDayInformation;
      });
    }
  },
  /**
   * Formats categories for display, sorts by value, and groups excess into 'other'.
   * @param {Array} data - List of category data.
   * @param {Function} t - Translation function.
   * @returns {Array} Formatted categories with color.
   */
  formatCategories: (data, t) => {
    let categories = data.map(i => ({name: i.category?.name, value: i.amount || 0})).sort((a, b) => b.value - a.value);
    const visibleCount = 5;
    if (categories.length > visibleCount) {
      const otherCategories = [...categories].splice(visibleCount);
      categories = [
        ...categories.slice(0, visibleCount),
        {
          name: t('other'),
          value: otherCategories.reduce((n, {value}) => n + value, 0),
        }
      ];
    }
    // add colors to pie chart item
    categories = categories.map((c, index) => ({...c, color: colorHelpers.getColorByIndex(index)}))
    return categories;
  },
  /**
   * Formats cost center data, separating over-budget and in-budget spendings.
   * @param {Array} data - List of cost center data.
   * @returns {Array} Formatted cost center data.
   */
  formatCostCenter: (data) => {
    data = data.map(i => ({actual: i.amount || 0, expected: i.budget || 0, name: i.cost_center}));
    let overBudgetSpending = data.filter(d => d.actual - d.expected > 0).sort((a, b) => b.actual - a.actual);
    let inBudgetSpending = data.filter(d => d.expected - d.actual >= 0).sort((a, b) => b.actual - a.actual);
    return [...overBudgetSpending, ...inBudgetSpending];
  },
  /**
   * Formats chart data for services by day, including actual and expected spending.
   * @param {Object} params - Parameters for formatting.
   * @param {Array} params.actualSpending - List of actual spending data.
   * @param {Array} params.expectedSpending - List of expected spending data.
   * @returns {Array} Chart data for services.
   */
  formatChartDataServices: ({
    actualSpending = [],
    expectedSpending = [],
  } = {}) => {
    const daysInMonth = dayjs().daysInMonth();

    return objectHelpers.range(1, daysInMonth).map(day => {
      const currentDaySpending = actualSpending.find(i => dayFilter(i, day));
      const expectedDaySpending = expectedSpending.find(i => dayFilter(i, day));

      let actual = [];
      let expected = [];
      let actualSpend = 0;
      let expectedSpend = 0;

      if (currentDaySpending) {
        // Actual spending
        actual = getSpendingServicesList(currentDaySpending);
        actualSpend = getSpendingServicesAmount(currentDaySpending);

        // Expected spending
        if (expectedDaySpending) {
          expected = getSpendingServicesList(expectedDaySpending);
          expectedSpend = getSpendingServicesAmount(expectedDaySpending);
        } else {
          expected = getSpendingServicesList(currentDaySpending, 'expected_amount');
          expectedSpend = getSpendingServicesAmount(currentDaySpending, 'expected_amount');
        }
      }

      return {
        day,
        actual,
        actualSpend,
        expected,
        expectedSpend,
      };
    });
  },
  /**
   * Formats yearly spending by services, sorts by actual amount, and limits to top 15.
   * @param {Array} actualSpending - List of actual spending data.
   * @param {Array} expectedSpending - List of expected spending data.
   * @returns {Array} Formatted yearly spending by services.
   */
  formatYearlyByServicesSpending: (actualSpending = [], expectedSpending = []) => {
    if (!actualSpending.length) return [];

    return actualSpending
      .map((actualItem) => {
        // Format the spending item and calculate the expected amount
        return {
          ...getFormattedSpending(actualItem),
          expected: actualItem?.expected_amount ?? 0,
        };
      })
      .sort((a, b) => b.actual - a.actual)
      .slice(0, 15);
  },
  /**
   * Formats yearly spending for the last 12 months.
   * @param {Array} actualSpending - List of actual spending data.
   * @param {Array} expectedSpending - List of expected spending data.
   * @returns {Array} Formatted yearly spending data.
   */
  formatYearlySpending: (actualSpending, expectedSpending) => {
    const startDate = dayjs().subtract(11, 'month'); // Starting month
    const spending = [];

    objectHelpers.range(0, 11).forEach(offset => {
      const currentDate = startDate.add(offset, 'month');
      const currentMonth = currentDate.month() + 1; // Month is zero-based in dayjs

      const actualSpendingData = actualSpending.find(({ month }) => month === currentMonth);
      const expectedSpendingData = expectedSpending.find(({ month }) => month === currentMonth);

      spending.push({
        label: currentDate.format('MMM'), // Format month label (e.g., "Jan")
        actual: actualSpendingData?.amount || 0,
        expected: expectedSpendingData?.amount || actualSpendingData?.expected_amount || 0,
      });
    });

    return spending;
  },
  /**
   * Formats variations between actual and expected spending, sorted by difference.
   * @param {Array} data - List of spending data.
   * @returns {Array} Formatted variations.
   */
  formatVariations: (data) => data
    .map(i => ({...getFormattedSpending(i), expected: i.expected_amount || 0, subscription_id: i.subscription_id}))
    .filter(i => i.expected !== i.actual)
    .sort((a, b) => (a.expected - a.actual) - (b.expected - b.actual)),
  /**
   * Formats upcoming spending data for the current or given month.
   * @param {Array} data - List of upcoming spending data.
   * @param {Object} currentMonth - Dayjs object for the current month.
   * @returns {Array} Formatted upcoming spending data.
   */
  formatUpcoming: (data, currentMonth) => {
    if (currentMonth === undefined) currentMonth = dayjs();
    return data
      .map(i => ({...getFormattedSpending(i), day: i.day, date: currentMonth.date(i.day).format('DD/MM/YY')}))
      .sort((a, b) => a.day - b.day);
  },
  /**
   * Extracts and merges spending data from the provided data object.
   * @param {Object} data - Data object containing spending arrays.
   * @returns {Object} Object with merged spending, upcoming, yearly, and yearlyByServices data.
   */
  getSpendingData: (data) => {
    const gDataP = (name) => data?.[name] || [];

    const spending = [...gDataP('spending'), ...gDataP('personal_spending')];
    const yearlyByServices = [...gDataP('spending_by_service'), ...gDataP('personal_spending_by_card')];

    return {
      spending: overviewPageUtils.mergeSpendingByDay(spending),
      upcoming: gDataP('upcoming'),
      yearly: overviewPageUtils.mergeSpendingByMonth(spending),
      yearlyByServices
    }
  },
  /**
    * Returns merged spending data for a specific user, filtered by employee email.
    * @param {Object} params - Parameters object.
    * @param {Object} params.data - Data object containing spending arrays.
    * @param {string} params.employeeEmail - Employee email to filter spending by.
    * @param {Object} [params.query={}] - Query object with month and year for date formatting.
    * @returns {Array} Merged spending data for the user.
    */
  getUserSpending: ({data, employeeEmail, query = {}} = {}) => {
    const gDataP = (name) => data?.[name] || [];
    const {month, year} = query;

    const spending = [...gDataP('spending'), ...gDataP('personal_spending')].map(dayObj => ({
      ...dayObj,
      date: `${dayObj?.day}-${query.month}-${query.year}`,
      expenses: (dayObj.expenses || []).filter(e => e.owner?.email === employeeEmail),
      services: (dayObj.services || []).filter(s => s.subscription_owner?.email === employeeEmail),
    }));

    // add spending if some days in current month are missing
    if (month && year) {
      const daysInMonth = dayjs().set('month', month - 1).set('year', year).daysInMonth();
      for (let day = 1; day <= daysInMonth; day++) {
        if (!spending.find(d => d.day === day)) {
          spending.push({
            day,
            date: `${day}-${month}-${year}`,
            services: [],
            expenses: []
          });
        }
      }
    }

    return overviewPageUtils.mergeSpendingByDay(spending)
  },
  /**
    * Merges spending data by day, combining services and expenses for each day.
    * @param {Array} data - List of daily spending data.
    * @returns {Array} Merged spending data by day.
    */
  mergeSpendingByDay: (data) => {
    return Object.values(
      data.reduce((merged, { day, date, services = [], expenses = [] }) => {
        merged[day] ??= { day, date, services: [], expenses: [] };
        merged[day].services.push(...services);
        merged[day].expenses.push(...expenses);
        return merged;
      }, {})
    );
  },
  /**
   * Merges spending data by month, summing amounts and expected amounts for each month.
   * @param {Array} data - List of monthly spending data.
   * @returns {Array} Merged spending data by month.
   */
  mergeSpendingByMonth: (data) => {
    return Object.values(
      data.reduce((merged, { month, amount = 0, expected_amount = 0 }) => {
        merged[month] ??= { month, amount: 0, expected_amount: 0 };
        merged[month].amount += amount;
        merged[month].expected_amount += expected_amount;
        return merged;
    }, {})
    );
  },
  /**
   * Formats a Dayjs date object into a string in the format "D-M-YYYY".
   * @param {Object} date - Dayjs date object.
   * @returns {string} Formatted date string or empty string if date is falsy.
   */
  getSpendingDateString: (date) => {
    if (!date) return '';
    const day = date.date();
    const month = date.month() + 1;
    const year = date.year();
    return `${day}-${month}-${year}`;
  },
  /**
   * Sums the amounts from the 'expenses' and 'services' arrays in the given data object.
   * @param {Object} data - Object containing 'expenses' and/or 'services' arrays.
   * @returns {number} Total sum of amounts.
   */
  sumSpendingAmounts: (data) => {
    let total = 0;
    if (data) {
      for (const key of ['expenses', 'services']) {
        if (Array.isArray(data[key])) {
          for (const item of data[key]) {
            total += Number(item.amount) || 0;
          }
        }
      }
    }
    return total;
  },
  /**
   * Calculate actual and expected spending for the latest 30 days.
   * @param {Array} thisMonthSpending - Spending list for this month.
   * @param {Array} prevMonthSpending - Spending list for previous month.
   * @returns {{actual: number, expected: number}}
   */
  calculateSpendingLast30Days: (thisMonthSpending, prevMonthSpending) => {
    const spendingMap = new Map();
    for (const item of prevMonthSpending) {
      spendingMap.set(item.date, item);
    }
    for (const item of thisMonthSpending) {
      spendingMap.set(item.date, item);
    }

    const daysToAdd = 30;
    const startDate = dayjs().subtract(daysToAdd - 1, 'day');

    let actual = 0;
    let expected = 0;

    for (let i = 0; i < daysToAdd; i++) {
      const date = startDate.add(i, 'day');
      const todayStr = overviewPageUtils.getSpendingDateString(date);
      const thirtyDaysAgo = date.subtract(30, 'day');
      const thirtyDaysStr = overviewPageUtils.getSpendingDateString(thirtyDaysAgo);
      const todaySpending = spendingMap.get(todayStr);
      const thirtyDaysAgoSpending = spendingMap.get(thirtyDaysStr);

      actual += overviewPageUtils.sumSpendingAmounts(todaySpending);
      expected += overviewPageUtils.sumSpendingAmounts(thirtyDaysAgoSpending);
    }

    return { actual, expected };
  },
  calculateSpendingPercentage: (actual, expected) => {
    if (expected === 0) return actual === 0 ? 0 : 100;
    return ((actual - expected) / expected) * 100;
  }
};

export {
  dateFilterRanges,
  expendituresTypes,
  overviewPageUtils,
}
